import { BADGE_VARIANTS } from 'afl-design-system';

const { MATCH_STATUS, MATCH_TIME_FORMAT, MAX_PERIOD_NUMBER } =
    PULSE.app.common.CONSTANTS;

const WEEKDAY_LABEL_MAX_DAYS = 7;
const HALF_TIME_PERIOD = 2;
const QUARTER_LABEL_KEYS = [
    'label.firstQuarter',
    'label.secondQuarter',
    'label.thirdQuarter',
    'label.fourthQuarter',
    'label.et1Quarter',
    'label.et2Quarter'
];

const STATUS_VARIANTS = {
    [MATCH_STATUS.CANCELLED]: BADGE_VARIANTS.VARIANT.CANCELLED,
    [MATCH_STATUS.POSTPONED]: BADGE_VARIANTS.VARIANT.POSTPONED,
    [MATCH_STATUS.TBC]: BADGE_VARIANTS.VARIANT.TBC,
    [MATCH_STATUS.COMPLETED]: BADGE_VARIANTS.VARIANT.FULL_TIME,
    [MATCH_STATUS.LIVE]: BADGE_VARIANTS.VARIANT.LIVE
};

// Live and pre-game are absent: their labels need the clock / start time.
const STATUS_LABEL_KEYS = {
    [MATCH_STATUS.CANCELLED]: 'label.fixtures.cancelled',
    [MATCH_STATUS.POSTPONED]: 'label.fixtures.postponed',
    [MATCH_STATUS.TBC]: 'label.fixtures.tbc',
    [MATCH_STATUS.COMPLETED]: 'label.fixtures.ft'
};

/**
 * A match's start time in the zone the user reads the site in.
 *
 * @param {string} utcStartTime - The match's UTC start time.
 * @param {string} [timezone] - Zone from the region setting; falls back to the device zone.
 * @returns {object} { start, daysUntil } — the zoned start and whole calendar days until it.
 */
function getZonedStart(utcStartTime, timezone) {
    const zone = timezone || moment.tz.guess(true);
    const start = moment(utcStartTime).tz(zone);

    return {
        start,
        daysUntil: start
            .clone()
            .startOf('day')
            .diff(moment().tz(zone).startOf('day'), 'days')
    };
}

/**
 * "Today" / "Sun", falling back to "27 Sep" once the match is more than a week
 * out. Rendered as the Mobile tile's separate date line, and folded into the
 * Desktop row's single pill label (see formatPreGameLabel).
 *
 * @param {string} utcStartTime - The match's UTC start time.
 * @param {string} [timezone] - Zone from the region setting; falls back to the device zone.
 * @returns {string} The formatted day/date label.
 */
export function formatMatchDayLabel(utcStartTime, timezone) {
    const { start, daysUntil } = getZonedStart(utcStartTime, timezone);

    if (daysUntil === 0) {
        return PULSE.I18N.lookup('label.fixtureBar.today');
    }

    return start.format('ddd, D MMM');
}

/**
 * The time on its own, e.g. "7:10pm", for the Mobile tile's compact pill.
 *
 * @param {string} utcStartTime - The match's UTC start time.
 * @param {string} [timezone] - Zone from the region setting; falls back to the device zone.
 * @returns {string} The formatted time.
 */
function formatPreGameTime(utcStartTime, timezone) {
    return getZonedStart(utcStartTime, timezone).start.format(
        `${MATCH_TIME_FORMAT}a`
    );
}

/**
 * "Today 7:10pm" / "Sun 7:10pm" / "27 Sep" for the Desktop row's combined pill.
 *
 * @param {string} utcStartTime - The match's UTC start time.
 * @param {string} [timezone] - Zone from the region setting; falls back to the device zone.
 * @returns {string} The formatted pre-game label.
 */
function formatPreGameLabel(utcStartTime, timezone) {
    const day = formatMatchDayLabel(utcStartTime, timezone);
    const { daysUntil } = getZonedStart(utcStartTime, timezone);

    if (daysUntil >= WEEKDAY_LABEL_MAX_DAYS) {
        return day;
    }

    return `${day} ${formatPreGameTime(utcStartTime, timezone)}`;
}

/**
 * "Half Time", "Full Time" or "Start of 2nd Quarter".
 *
 * @param {object} period - The latest period from the match clock.
 * @param {'start'|'end'} phase - Whether the period just started or just ended.
 * @returns {string|null} The label, or null past the labels we have so the
 * caller falls back to the live clock.
 */
function formatPeriodEventLabel(period, phase) {
    if (phase === 'end') {
        if (period.periodNumber === HALF_TIME_PERIOD) {
            return PULSE.I18N.lookup('label.fixtures.halfTime');
        }

        if (period.periodNumber === MAX_PERIOD_NUMBER) {
            return PULSE.I18N.lookup('label.fixtures.fullTime');
        }
    }

    const prefix =
        phase === 'end'
            ? PULSE.I18N.lookup('label.fixtureBar.endOf')
            : PULSE.I18N.lookup('label.fixtureBar.startOf');
    const quarterKey = QUARTER_LABEL_KEYS[period.periodNumber - 1];

    return quarterKey ? `${prefix} ${PULSE.I18N.lookup(quarterKey)}` : null;
}

/**
 * Pure state→variant mapping from the "Tiny Match Card".
 *
 * @param {object} params - Params.
 * @param {string} params.genericStatus - One of PULSE.app.common.CONSTANTS.MATCH_STATUS.
 * @param {object} [params.scoreUpdate] - { show, team: 'home'|'away' } from useScoreUpdate.
 * @returns {string} A BADGE_VARIANTS.VARIANT value.
 */
export function getStatusVariant({ genericStatus, scoreUpdate }) {
    if (scoreUpdate?.show) {
        return scoreUpdate.team === 'home'
            ? BADGE_VARIANTS.VARIANT.CLUB_HOME
            : BADGE_VARIANTS.VARIANT.CLUB_AWAY;
    }

    return STATUS_VARIANTS[genericStatus] ?? BADGE_VARIANTS.VARIANT.UPCOMING;
}

/**
 * @param {object} scoreUpdate - { teamAbbr, type } from useScoreUpdate.
 * @returns {object} Badge content tinted with the scoring club's colour.
 */
function getScoreUpdateContent(scoreUpdate) {
    const clubColor = `var(--${scoreUpdate.teamAbbr})`;

    return {
        label:
            scoreUpdate.type === 'GOAL'
                ? PULSE.I18N.lookup('label.fixtureBar.goal')
                : PULSE.I18N.lookup('label.mc.stats.behind'),
        className: 'animate-score-update',
        style: {
            '--color-club-primary': clubColor,
            '--color-club-secondary': clubColor
        }
    };
}

/**
 * @param {object} params - Params.
 * @param {object} [params.periodEvent] - { show, period, phase } from usePeriodEvent.
 * @param {object} params.timeString - The live clock string from the match store.
 * @param {boolean} params.compact - Mobile tile mode.
 * @returns {object} Badge content: the running clock, or a flashed period change.
 */
function getLiveContent({ periodEvent, timeString, compact }) {
    const periodLabel = periodEvent?.show
        ? formatPeriodEventLabel(periodEvent.period, periodEvent.phase)
        : null;

    if (!periodLabel) {
        return { label: timeString.string, clock: timeString.clock };
    }

    // Compact keeps the clock's own short break label — the long form
    // ("Start of 2nd Quarter") overflows the mobile tile's pill.
    return {
        label: compact ? timeString.string : periodLabel,
        clock: compact ? timeString.clock : null,
        className: 'animate-score-update'
    };
}

/**
 * The badge's label and any extra Badge props for the match's current state,
 * per the "Tiny Match Card" state -> Badge table.
 *
 * @param {object} params - Params.
 * @param {object} params.match - The normalised match object from useMatchCardData.
 * @param {string} params.genericStatus - One of PULSE.app.common.CONSTANTS.MATCH_STATUS.
 * @param {boolean} [params.isPlaceholder] - True when either team is a placeholder (unknown teams).
 * @param {object} [params.scoreUpdate] - { show, team, teamAbbr, type } from useScoreUpdate.
 * @param {object} [params.periodEvent] - { show, period, phase } from usePeriodEvent.
 * @param {object} [params.timeString] - The live clock string from the match store.
 * @param {string} [params.timezone] - Zone from the region setting.
 * @param {boolean} [params.compact] - Mobile tile mode: pre-game shows just the
 * time ("7:10pm"), the day/date being rendered separately by the caller.
 * @returns {object} { label, className, style } for the Badge.
 */
export function getStatusContent({
    match,
    genericStatus,
    scoreUpdate,
    periodEvent,
    timeString,
    timezone,
    compact
}) {
    if (scoreUpdate?.show) {
        return getScoreUpdateContent(scoreUpdate);
    }

    const labelKey =
        genericStatus === MATCH_STATUS.TBC
            ? STATUS_LABEL_KEYS[MATCH_STATUS.TBC]
            : STATUS_LABEL_KEYS[genericStatus];

    if (labelKey) {
        return { label: PULSE.I18N.lookup(labelKey) };
    }

    if (genericStatus === MATCH_STATUS.LIVE) {
        return getLiveContent({ periodEvent, timeString, compact });
    }

    return {
        label: compact
            ? formatPreGameTime(match.utcStartTime, timezone)
            : formatPreGameLabel(match.utcStartTime, timezone)
    };
}
